import { isEmpty } from 'lodash';

import type { NavigateTo } from '@core/app/scripts/services/navigate-to';
import { DisplayMessage } from '@core/app/scripts/services/display-message';
import { HandleData } from '@core/app/scripts/services/handle-data';
import { HandleTemplate } from '@pos/app/scripts/services/handle-template';
import { Printer } from '@shared/app/scripts/services/printer';
import { SettingsManager } from '@shared/app/scripts/services/settings-manager';

/**
 * @ngdoc service
 * @name posApp.legacyPrint
 * @description
 * Service for mixing templates with data and sending them to printer. On init
 * it loads all settings related to that.
 */
angular
  .module('posApp')
  .factory(
    'legacyPrint',
    (
      $timeout: ng.ITimeoutService,
      $q: ng.IQService,
      dateFilter: ng.IFilterDate,
      displayMessage: DisplayMessage,
      handleData: HandleData,
      handleTemplate: HandleTemplate,
      navigateTo: NavigateTo,
      printer: Printer,
      sales,
      salesCalculations,
      translate: ng.translate.ITranslateService,
      utility,
      settingsManager: SettingsManager,
    ) => {
      const $translate = translate;

      const legacyPrint = {
          /**
           * @ngdoc
           * @name posApp.legacyPrint#settings
           * @propertyOf posApp.legacyPrint
           * @deprecated
           * @description
           * Settings collection, in same format as loaded from the server. Used by
           * printController to edit the settings.
           *
           * Service keeps a reference to the array unchanged.
           *
           * # Deprecated
           * It's exposed for legacy unit tests. No new code should depend on that. It
           * has to be replace by better modularization.
           */
          settings: [],

          /**
           * @ngdoc
           * @name posApp.legacyPrint#templates
           * @propertyOf posApp.legacyPrint
           * @deprecated
           * @description
           * Template related settings. To become private after finishing the
           * refactoring.
           *
           * # Deprecated
           * It's exposed for legacy unit tests. No new code should depend on that. It
           * has to be replace by better modularization.
           */
          templates: {},

          /**
           * @ngdoc
           * @name posApp.legacyPrint#id
           * @propertyOf posApp.legacyPrint
           * @deprecated
           * @description
           * Id received form server, while loading data. Meaning unclear.
           * To be removed after finishing refactoring.
           *
           * # Deprecated
           * It's exposed for legacy unit tests. No new code should depend on that. It
           * has to be replace by better modularization.
           */
          id: undefined,
        },
        PQ_DELAY = 1000;

      const getFooterText = () => {
        if (!isEmpty(settingsManager.settings.additionalPrintInfo?.receipt)) {
          return settingsManager.settings.additionalPrintInfo.receipt;
        }

        return '';
      };

      const getPrintLoyaltyPointsOnReceiptSetting = () => {
        return settingsManager.settings.loyalty?.printPointsOnReceipt;
      };

      // This setting turns on/off the loyalty point system
      const getUseLoyaltyPointsSystem = () => {
        return settingsManager.settings.loyalty.calculatePointsPerReceipt;
      };

      const getExtendedReceiptInfoSetting = () => {
        return settingsManager.settings.pos?.extendedReceipt;
      };

      const getSettings = () => {
        return {
          footer: getFooterText(),
          loyaltyRule: getUseLoyaltyPointsSystem(),
          receiptPrintpoints: getPrintLoyaltyPointsOnReceiptSetting(),
          extended: getExtendedReceiptInfoSetting(),
          vatHigh: settingsManager.settings.vat.highvalue,
          vatLow: settingsManager.settings.vat.lowvalue,
          vatZero: settingsManager.settings.vat.zerovalue,
        };
      };

      /**
       * @ngdoc
       * @name posApp.legacyPrint#transitOrder
       * @methodOf posApp.legacyPrint
       * @description
       * method to take the template, fill it up with data & send to printer
       * @param {object} container
       * object containing all data to be provided in the template
       * @return {promise} resolve on printing success, fails on broken printing or
       * template loading
       */
      legacyPrint.transitOrder = (container) => {
        const date = new Date();

        container.transactions.printed = dateFilter(date, 'short');

        let template;

        if (settingsManager.settings.printer === 'browser') {
          template = 'transit_html.mustache';
        } else {
          template = 'transit.mustache';
        }

        return handleTemplate
          .renderObject(template, container)
          .then((transitData: string) => {
            if (container?.send === true) {
              return printer.useDefault(transitData, true);
            } else {
              navigateTo('pos.route#pos');

              return printer.useDefault(transitData, true);
            }
          });
      };

      /**
       * @ngdoc
       * @name posApp.legacyPrint#preformattedData
       * @methodOf posApp.legacyPrint
       * @description
       * method to take the template, fill it up with data & send to printer
       * @param {string} data
       * string to be printer
       * @return {promise} resolves or fails on printing
       */
      legacyPrint.preformattedData = (data) => {
        return printer.useDefault(data, true);
      };

      /**
       * @ngdoc
       * @name posApp.legacyPrint#emailReceipt
       * @methodOf posApp.legacyPrint
       * @deprecated
       * @description
       * method to send the sales transaction as an email
       *
       * # Deprecated
       * This method was coupled with printing code, but the it's only sending
       * emails. It should be moved to more appropriate place.
       * @param {string} transactionId
       * transaction id of the sales transaction
       * @param {string} email
       * address where the email will be sent
       * @return {promise} resolves or fails on mailing
       */
      legacyPrint.emailReceipt = (transactionId, email) => {
        const deferred = $q.defer();

        // The bottom text printed on the receipt
        const footer = getFooterText();
        // Both settings together affect if the loyalty points are printed
        const printLoyaltyPoints =
          getUseLoyaltyPointsSystem() &&
          getPrintLoyaltyPointsOnReceiptSetting();
        // Print a second row of additional product information.
        // Legacy setting which is stored as a string "true"/"false"
        const printExtendedReceipt = getExtendedReceiptInfoSetting() === 'true';

        if (!settingsManager.settings.email?.active) {
          return $q.reject($translate.instant('MissingEmailingModule'));
        }

        sales
          .email(
            transactionId,
            email,
            footer,
            printLoyaltyPoints,
            printExtendedReceipt,
          )
          .then(
            () => {
              deferred.resolve();
            },
            (error) => {
              deferred.reject(error.data.message);
            },
          );

        return deferred.promise;
      };

      /**
       * @ngdoc
       * @name posApp.legacyPrint#receipt
       * @methodOf posApp.legacyPrint
       * @description
       * method to take the template, fill it up with data & send it to the printer
       *
       * @param {object} otransaction
       * transaction that will be sent to the user
       * @param {boolean} reprint
       * deprecated - true if the transaction is being printed again, false form
       * printing for the first time
       * @return {promise} resolves or fails on the main printing. Ignores result of
       * printing creditreceipt & a copy.
       */
      legacyPrint.receipt = (transaction, reprint, skipReceipt?: boolean) => {
        if (skipReceipt) {
          return $q.when();
        }

        let printOut = handleTemplate.renderReceipt(transaction, reprint);

        let printResult = printer.useDefault(
          printOut,
          true,
          transaction.payments?.cash && !reprint,
        );

        // print receipt copy
        if (settingsManager.settings.pos?.printDoubleReceipt && !reprint) {
          $timeout(() => {
            printer.useDefault(
              $translate.instant('Copy Receipt').toUpperCase() +
                '\n' +
                printOut,
              true,
            );
          }, PQ_DELAY);
        }

        return printResult;
      };

      /**
       * @ngdoc
       * @name posApp.legacyPrint#receiptRaw
       * @methodOf posApp.legacyPrint
       * @description
       * Prints transaction object as it is—without manipulation.
       * @param {object} transaction
       * transaction that will be printeda.
       */
      legacyPrint.receiptRaw = (transaction) => {
        return printer.useDefault(
          handleTemplate.renderReceipt(transaction),
          true,
          false,
        );
      };

      /**
       * @ngdoc
       * @name posApp.legacyPrint#getCouponLabel
       * @methodOf posApp.legacyPrint
       * @description
       * Returns a customer-oriented label for a coupon based on the coupon code
       * @param {string} type
       * string with the coupon type, 'K', 'C', 'U', 'A' or ' '
       * @return {string} customer-oriented label for the coupon
       */
      legacyPrint.getCouponLabel = (type) => {
        switch (type) {
          case 'C':
            return 'Cashback Coupon';
          case 'T':
            return 'Credit Receipt';
          case 'U':
            return 'Gift Card';
          case 'A':
          case ' ':
            return 'Discount Coupon';
          default:
            return 'Coupon';
        }
      };

      /**
       * @ngdoc
       * @name posApp.legacyPrint#printCoupon
       * @methodOf posApp.legacyPrint
       * @description
       * Pulls the coupon through the template handler and sends it to the printer.
       * @param {string} coupon transaction-like object containing all data needed
       * for printing the coupon
       */
      // TODO refactor out the method
      legacyPrint.printCoupon = (coupon) => {
        let template;

        if (settingsManager.settings.printer === 'browser') {
          template = 'coupon-laser-printer_html.mustache';
        } else {
          template = 'coupon-plain.mustache';
        }

        $timeout(() => {
          handleTemplate.renderObject(
            template,
            {
              transactions: coupon,
              printed: coupon.printed,
              additionalInfo: coupon.additionalInfo,
              labels: {
                title: legacyPrint.getCouponLabel(coupon.coupon.type),
              },
            },
            (template) => {
              return printer.useDefault(template, true);
            },
          );
        }, PQ_DELAY);
      };

      /**
       * @ngdoc
       * @name posApp.legacyPrint#eos
       * @methodOf posApp.legacyPrint
       * @description
       * Method to take the template, fill it up with data & send it to the printer.
       * It sends the email too, if address provided.
       * @param {object} eos
       * eos object that will be printed for the user
       * @param {string} deviceName
       * name of the device sending the EOS
       * @param {string} warehouseName
       * name of the warehouse sending the EOS
       * @param {string} cashierName
       * name of the cashier sending the EOS
       * @return {promise} successes @ printing (& mailing), fails if any of them
       * fails
       */
      legacyPrint.eos = (eos, deviceName, warehouseName, cashierName) => {
        eos.final.cashtopay = eos.final.cash - eos.final.change;

        const container = {
            eos: eos,
          },
          promises = [];

        let template;

        if (settingsManager.settings.printer === 'browser') {
          template = 'eos_html.mustache';
        } else {
          template = 'eos.mustache';
        }

        promises[0] = handleTemplate
          .renderObject(template, container)
          .then((compiledTemplate: string) => {
            return printer.useDefault(compiledTemplate, true, false);
          });

        return $q.all(promises);
      };

      /**
       * @ngdoc
       * @name posApp.legacyPrint#withdrawal
       * @methodOf posApp.legacyPrint
       * @description
       * Method to take the template, fill it up with data & send it to the printer.
       * @param {object} otransaction
       * transactions object that will be printed for the user
       * @param {object} config
       * object that will be available under 'config' name on the template
       * @param {function} callback
       * deprecated - the method that will be called on the successful mailing
       * @return {promise} successes fails @ printing
       */
      legacyPrint.withdrawal = (otransaction, config, callback) => {
        const transaction = angular.copy(otransaction);
        //
        // print Credit Receipt
        const receiptData = '';
        let title = '';

        if (transaction.payments.cash > 0) {
          title = $translate.instant('Deposit');
        } else if (transaction.payments.cash < 0) {
          title = $translate.instant('Withdrawal');
        } else if (transaction.payments.cash === 0) {
          title = $translate.instant('OpenDrawer');
        }

        const date = new Date();
        const settings = {
          cashtype: title,
          title,
          printed: dateFilter(date, 'short'),
          amount: Math.abs(transaction.payments.cash),
          //lets not just put the entire object available.
          cashier: {
            name: config.selectedCashier.name,
            fullname: config.selectedCashier.fullname,
          },
        };
        const container = {
          settings: settings,
          transactions: transaction,
        };

        let printOut = handleTemplate.renderWithdrawal(container);

        let printResult = printer.useDefault(printOut, true, true);

        // print receipt
        return printResult;
      };

      return legacyPrint;
    },
  );
